import QuestionMark from "@/assets/svg/creator-shield/question-mark.svg";
import Rocket from "@/assets/svg/creator-shield/rocket.svg";
import Star from "@/assets/svg/creator-shield/star.svg";
import Telephone from "@/assets/svg/creator-shield/telephone.svg";
import Footer from "@/components/creatorshield/Footer";
import { CSPrimaryBtn } from "@/components/design-system/creatorshield/CSButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

export default function CreatorshieldContactUs() {
  const headingRef = useScrollAnimation({ threshold: 0.2 });
  
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    userType: "Creator",
    message: "",
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (value) => {
    setFormData({ ...formData, userType: value });
  };

  const sendMail = () => {
    const subject = encodeURIComponent("CreatorShield Website: Inquiries");
    const body = encodeURIComponent(
      `Name: ${formData.firstName} ${formData.lastName}
Email: ${formData.email}
User Type: ${formData.userType}
Message:
${formData.message}`
    );

    const gmailURL = `https://mail.google.com/mail/?view=cm&fs=1&to=requests@enfinity.com&su=${subject}&body=${body}`;
    window.open(gmailURL, "_blank");

    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      userType: "Creator",
      message: "",
    });
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  return (
    <>
      <div className="bg-white pt-28 lg:pt-40 pb-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex justify-center mb-9 mt-[20px] lg:mb-9 lg:mt-[-30px]">
            <div className="flex items-center gap-2 border border-border rounded-full px-4 py-2">
              <img src={Telephone} alt="Telephone Icon" className="w-5 h-5" />
              <span className="font-normal leading-normal whitespace-nowrap text-[#FF6B35]">
                We're here to help!
              </span>
            </div>
          </div>

          {/* Main Heading */}
          <div ref={headingRef} className="text-center mb-12 animate-fade-rise">
            <h1 className="text-6xl lg:text-7xl font-semibold mb-6">
              Get in <span className="text-[#FF6B35]">Touch</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-xl mx-auto">
              Have questions? Need enterprise solutions? Our team is ready to
              help you protect your content and maximize your revenue.
            </p>
          </div>

          {/* Contact Form */}
          <div className="bg-white border border-[#ddd] rounded-[20px] max-w-2xl mx-auto mb-20 overflow-hidden no-animation">
            <div className="p-10 space-y-2">
              <h2 className="text-2xl font-normal text-black">
                Send us a message
              </h2>
              <p className="text-base text-[rgba(0,0,0,0.6)]">
                Fill out the form below and we'll respond within 24 hours
              </p>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  sendMail();
                }}
                className="flex flex-col gap-[25px] !mt-8"
              >
                <div className="flex flex-col md:flex-row gap-[27px]">
                  <div className="flex flex-col gap-[10px] flex-1">
                    <Label className="text-base font-medium text-black">
                      First Name
                    </Label>
                    <Input
                      type="text"
                      name="firstName"
                      placeholder="Ex. John"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="p-6 rounded-[10px] bg-white border border-[#ddd] text-base text-black placeholder:text-[rgba(0,0,0,0.5)] focus-visible:ring-2 focus-visible:ring-[#FF6B35]"
                      required
                    />
                  </div>
                  <div className="flex flex-col gap-[10px] flex-1">
                    <Label className="text-base font-medium text-black">
                      Last Name
                    </Label>
                    <Input
                      type="text"
                      name="lastName"
                      placeholder="Ex. Roberts"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className="p-6 rounded-[10px] bg-white border border-[#ddd] text-base text-black placeholder:text-[rgba(0,0,0,0.5)] focus-visible:ring-2 focus-visible:ring-[#FF6B35]"
                      required
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-[10px]">
                  <Label className="text-base font-medium text-black">
                    Email Address
                  </Label>
                  <Input
                    type="email"
                    name="email"
                    placeholder="Please enter a valid email address"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="p-6 rounded-[10px] bg-white border border-[#ddd] text-base text-black placeholder:text-[rgba(0,0,0,0.5)] focus-visible:ring-2 focus-visible:ring-[#FF6B35]"
                    required
                  />
                </div>

                <div className="flex flex-col gap-[10px]">
                  <Label className="text-base font-medium text-black">
                    I'm a
                  </Label>
                  <Select
                    value={formData.userType}
                    onValueChange={handleSelectChange}
                  >
                    <SelectTrigger className="p-6 w-full rounded-[10px] bg-white border border-[#ddd] text-base text-black focus:ring-2 focus:ring-[#FF6B35]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white p-4 text-base">
                      <SelectItem value="Creator">Creator</SelectItem>
                      <SelectItem value="Media Company">
                        Media Company
                      </SelectItem>
                      <SelectItem value="Enterprise">Enterprise</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col gap-[10px]">
                  <Label className="text-base font-medium text-black">
                    Message
                  </Label>
                  <Textarea
                    name="message"
                    placeholder="Tell us how we can help..."
                    value={formData.message}
                    onChange={handleInputChange}
                    className="min-p-6 rounded-[10px] bg-white border border-[#ddd] text-base text-black placeholder:text-[rgba(0,0,0,0.5)] focus-visible:ring-2 focus-visible:ring-[#FF6B35]"
                    required
                  />
                </div>

                <CSPrimaryBtn type="submit">
                  Send us a message
                  <ArrowRight className="w-6 h-6" />
                </CSPrimaryBtn>
              </form>
            </div>
          </div>

          {/* Quick Links Section */}
          <div className="mb-20">
            <h2 className="text-4xl md:text-5xl font-semibold text-center mb-12">
              Quick Links
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Start Earning Card */}
              <Link to="/esign" className="block">
                <div className="bg-white rounded-2xl border border-border p-8 text-center hover:border-[#FF6B35] transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-xl cursor-pointer">
                  <img
                    src={Rocket}
                    alt="Rocket Icon"
                    className="w-16 h-16 mx-auto mb-4"
                  />
                  <h3 className="text-xl font-bold mb-3">Start Earning</h3>
                  <p className="text-gray-600">
                    Sign up in under 2 minutes and start protecting your content
                  </p>
                </div>
              </Link>

              {/* Help Center Card */}
              <Link to="/faq" className="block">
                <div className="bg-white rounded-2xl border border-border p-8 text-center hover:border-[#FF6B35] transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-xl cursor-pointer">
                  <img
                    src={QuestionMark}
                    alt="Question Mark Icon"
                    className="w-16 h-16 mx-auto mb-4"
                  />
                  <h3 className="text-xl font-bold mb-3">Help Center</h3>
                  <p className="text-gray-600">
                    Browse our FAQs and knowledge base for quick answers
                  </p>
                </div>
              </Link>

              {/* Success Stories Card */}
              <Link to="/case-study" className="block">
                <div className="bg-white rounded-2xl border border-border p-8 text-center hover:border-[#FF6B35] transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-xl cursor-pointer">
                  <img
                    src={Star}
                    alt="Star Icon"
                    className="w-16 h-16 mx-auto mb-4"
                  />
                  <h3 className="text-xl font-bold mb-3">Success Stories</h3>
                  <p className="text-gray-600">
                    See how creators are earning with CreatorShield
                  </p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
